import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ChevronLeft, ChevronRight, Loader2, Search, ExternalLink, ArrowUpDown, Trash2, AlertTriangle } from 'lucide-react';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { getISOWeekNow } from './planning/utils';
import { ConfirmDialog } from './planning/ConfirmDialog';
import { normalizeTr } from './planning/utils';

/**
 * Planning → Listele (List view).
 *
 * Two-pane layout:
 *  • Top:    one row per (year, week) — counts, fill-rate, firm chips,
 *            "Aç" jump link to /dashboard/planning?year&week.
 *  • Bottom: full assignment list for the selected year — searchable +
 *            filterable + sortable.
 */
export default function PlanningList() {
  const _now = useMemo(getISOWeekNow, []);
  const [year, setYear] = useState(_now.year);
  const [summary, setSummary] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFirm, setFilterFirm] = useState('all');
  const [sortBy, setSortBy] = useState('date'); // date | employee | route
  // Two-step destructive confirmation state for "Tüm yılı sil".
  const [wipeStep, setWipeStep] = useState(null); // null | 'first' | 'second'
  const [wipeTyped, setWipeTyped] = useState('');
  const [wiping, setWiping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, a] = await Promise.all([
          api.get(`/planning/yearly-summary?year=${year}`),
          api.get(`/planning/assignments-list?year=${year}`).catch(() => null),
        ]);
        if (cancelled) return;
        setSummary(s.data?.data || null);
        // Fallback: if assignments-list endpoint not present, derive from /board per week.
        if (a && a.data?.success) {
          setAssignments(a.data.data || []);
        } else {
          setAssignments([]);
        }
      } catch {
        if (!cancelled) toast.error('Veri yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year]);

  // Pull all weeks that have data and any assignments for the bottom table.
  const weeksWithData = useMemo(
    () => (summary?.weeks || []).filter((w) => w.assigned > 0 || w.sunday_assigned > 0),
    [summary]
  );
  const totalAssignedForYear = useMemo(
    () => (summary?.weeks || []).reduce((s, w) => s + (w.assigned || 0) + (w.sunday_assigned || 0), 0),
    [summary]
  );
  const weeksWithDataCount = weeksWithData.length;

  const filteredAssignments = useMemo(() => {
    let list = assignments;
    if (filterStatus !== 'all') list = list.filter((a) => a.status === filterStatus);
    if (filterFirm !== 'all') list = list.filter((a) => a.firm_name === filterFirm);
    const q = normalizeTr(search.trim());
    if (q) {
      list = list.filter((a) =>
        normalizeTr(a.employee_name || '').includes(q) ||
        normalizeTr(a.route_code || '').includes(q) ||
        normalizeTr(a.company_name || '').includes(q) ||
        normalizeTr(a.firm_name || '').includes(q) ||
        normalizeTr(a.vehicle_code || '').includes(q)
      );
    }
    const sorted = [...list];
    if (sortBy === 'date') {
      sorted.sort((a, b) => (a.assignment_date || '').localeCompare(b.assignment_date || ''));
    } else if (sortBy === 'employee') {
      sorted.sort((a, b) => (a.employee_name || '').localeCompare(b.employee_name || '', 'tr'));
    } else if (sortBy === 'route') {
      sorted.sort((a, b) => (a.route_code || '').localeCompare(b.route_code || ''));
    }
    return sorted;
  }, [assignments, filterStatus, filterFirm, search, sortBy]);

  const firmsInList = useMemo(() => {
    const s = new Set();
    for (const a of assignments) if (a.firm_name) s.add(a.firm_name);
    return [...s].sort();
  }, [assignments]);

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4" data-testid="planning-list-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Planlama Listesi</h1>
        <div className="flex items-center gap-1 ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-900/20"
            onClick={() => { setWipeStep('first'); setWipeTyped(''); }}
            disabled={!summary || (summary.weeks || []).every((w) => w.assigned === 0)}
            data-testid="planning-list-wipe-year"
            title={`${year} yılının tüm atamalarını sil`}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Tüm yılı sil
          </Button>
          <Button variant="outline" size="icon" onClick={() => setYear(year - 1)} data-testid="planning-list-prev-year">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-3 py-1 rounded bg-slate-100 dark:bg-slate-800 text-sm font-semibold">{year}</div>
          <Button variant="outline" size="icon" onClick={() => setYear(year + 1)} data-testid="planning-list-next-year">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 ml-2" />}
        </div>
      </div>

      {/* Weekly summary table */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 text-sm font-semibold">
          Haftalık özet — {summary?.total_routes || 0} aktif rota
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-slate-900/40 text-[11px] uppercase text-slate-500">
                <th className="px-3 py-2 text-left">Hafta</th>
                <th className="px-3 py-2 text-left">Tarih aralığı</th>
                <th className="px-3 py-2 text-right">Atama</th>
                <th className="px-3 py-2 text-right">Pazar</th>
                <th className="px-3 py-2 text-right">Boş</th>
                <th className="px-3 py-2 text-right">Doluluk</th>
                <th className="px-3 py-2 text-left">Firmalar</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {(summary?.weeks || []).map((w) => {
                const hasData = w.assigned > 0 || w.sunday_assigned > 0;
                return (
                  <tr
                    key={w.week}
                    className={`border-b border-slate-100 dark:border-slate-800 ${hasData ? '' : 'opacity-50'}`}
                    data-testid={`planning-list-week-${w.week}`}
                  >
                    <td className="px-3 py-1.5 font-mono font-bold">W{w.week}</td>
                    <td className="px-3 py-1.5 text-slate-500">{w.start_date} → {w.end_date}</td>
                    <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">{w.assigned}</td>
                    <td className="px-3 py-1.5 text-right">{w.sunday_assigned > 0 ? <span className="text-amber-600">+{w.sunday_assigned}</span> : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-slate-500">{w.missing}</td>
                    <td className="px-3 py-1.5 text-right">
                      <FillBar pct={w.fill_rate} />
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(w.firms || {}).map(([fname, n]) => (
                          <span key={fname} className="text-[10px] px-1.5 py-0 rounded bg-indigo-50 text-indigo-700 font-semibold border border-indigo-200">
                            {fname} {n}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <Link
                        to={`/dashboard/planning?year=${w.year}&week=${w.week}`}
                        className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
                        data-testid={`planning-list-open-${w.week}`}
                      >
                        Aç <ExternalLink className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detailed assignment list (only weeks with data) */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">Tüm atamalar</span>
          <Badge variant="outline" className="text-[10px]">{filteredAssignments.length}</Badge>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Ara: çalışan / rota / plaka..."
                className="pl-7 h-8 w-64 text-xs"
                data-testid="planning-list-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Durum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm durumlar</SelectItem>
                <SelectItem value="assigned">Atandı</SelectItem>
                <SelectItem value="leave">İzinli</SelectItem>
                <SelectItem value="empty">Kapalı</SelectItem>
                <SelectItem value="depot">Depo</SelectItem>
                <SelectItem value="reserve">Yedek</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterFirm} onValueChange={setFilterFirm}>
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Firma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm firmalar</SelectItem>
                {firmsInList.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 w-32 text-xs">
                <ArrowUpDown className="h-3 w-3 mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Tarih</SelectItem>
                <SelectItem value="employee">Çalışan</SelectItem>
                <SelectItem value="route">Rota</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900 text-[10px] uppercase text-slate-500 z-10">
              <tr>
                <th className="px-2 py-1.5 text-left">Tarih</th>
                <th className="px-2 py-1.5 text-left">Hafta</th>
                <th className="px-2 py-1.5 text-left">Müşteri</th>
                <th className="px-2 py-1.5 text-left">Depo</th>
                <th className="px-2 py-1.5 text-left">Rota</th>
                <th className="px-2 py-1.5 text-left">Çalışan</th>
                <th className="px-2 py-1.5 text-left">Firma</th>
                <th className="px-2 py-1.5 text-left">Plaka</th>
                <th className="px-2 py-1.5 text-left">Durum</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="px-2 py-1 font-mono">{a.assignment_date}</td>
                  <td className="px-2 py-1 text-slate-500">W{a.week_number}</td>
                  <td className="px-2 py-1">{a.company_name || '—'}</td>
                  <td className="px-2 py-1 text-slate-500">{a.depot_name || '—'}</td>
                  <td className="px-2 py-1 font-semibold">{a.route_code || '—'}</td>
                  <td className="px-2 py-1">{a.employee_name || '—'}</td>
                  <td className="px-2 py-1">
                    {a.firm_name && (
                      <span className="text-[9px] px-1 py-0 rounded bg-indigo-100 text-indigo-700 font-semibold uppercase">{a.firm_name}</span>
                    )}
                  </td>
                  <td className="px-2 py-1 font-mono">{a.vehicle_code || '—'}</td>
                  <td className="px-2 py-1">
                    <StatusBadge status={a.status} />
                  </td>
                </tr>
              ))}
              {filteredAssignments.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-slate-400">Atama yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {weeksWithData.length === 0 && !loading && (
        <p className="text-center text-sm text-slate-400">Bu yıl için atama yok.</p>
      )}

      {/* ─── Two-step destructive confirmation for "Tüm yılı sil" ─── */}
      <ConfirmDialog
        open={wipeStep === 'first'}
        title={`${year} yılının TÜM atamalarını sil`}
        message={`${totalAssignedForYear} atama ${weeksWithDataCount} hafta boyunca kalıcı olarak silinecek.\n\nBu işlem geri alınamaz! Devam etmeden önce yedek almayı düşündün mü?`}
        confirmLabel="Devam et"
        destructive
        onClose={() => setWipeStep(null)}
        onConfirm={() => setWipeStep('second')}
      />

      {wipeStep === 'second' && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !wiping && setWipeStep(null)} data-testid="planning-list-wipe-confirm-second">
          <Card className="max-w-md w-full p-5 border-2 border-red-400" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h3 className="text-base font-bold text-red-700">Son onay — geri alınamaz</h3>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-200 mb-3">
              Onaylamak için aşağıya aynen <b className="font-mono text-red-700">SIL {year}</b> yazman gerekiyor.
            </p>
            <Input
              value={wipeTyped}
              onChange={(e) => setWipeTyped(e.target.value)}
              placeholder={`SIL ${year}`}
              className="font-mono text-sm"
              autoFocus
              data-testid="planning-list-wipe-phrase-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setWipeStep(null)} disabled={wiping}>
                Vazgeç
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={wipeTyped.trim() !== `SIL ${year}` || wiping}
                onClick={async () => {
                  setWiping(true);
                  try {
                    const res = await api.delete(`/planning/year-assignments?year=${year}`);
                    const n = res.data?.data?.deleted ?? 0;
                    toast.success(`${n} atama silindi`);
                    setWipeStep(null);
                    setWipeTyped('');
                    // Refresh both tables.
                    const [s, a] = await Promise.all([
                      api.get(`/planning/yearly-summary?year=${year}`),
                      api.get(`/planning/assignments-list?year=${year}`).catch(() => null),
                    ]);
                    setSummary(s.data?.data || null);
                    setAssignments(a?.data?.data || []);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Silinemedi');
                  } finally {
                    setWiping(false);
                  }
                }}
                data-testid="planning-list-wipe-confirm-btn"
              >
                {wiping ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                Evet, {year} yılını sil
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function FillBar({ pct }) {
  const v = Math.max(0, Math.min(100, pct || 0));
  const color = v >= 80 ? 'bg-emerald-500' : v >= 40 ? 'bg-amber-400' : v > 0 ? 'bg-sky-400' : 'bg-slate-200';
  return (
    <div className="inline-flex items-center gap-2">
      <div className="w-24 h-1.5 rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-xs font-semibold tabular-nums w-10 text-right">{v}%</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    assigned: ['Atandı', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
    empty:    ['Kapalı', 'bg-slate-100 text-slate-600 border-slate-300'],
    leave:    ['İzin',   'bg-amber-50 text-amber-700 border-amber-200'],
    depot:    ['Depo',   'bg-sky-50 text-sky-700 border-sky-200'],
    reserve:  ['Yedek',  'bg-violet-50 text-violet-700 border-violet-200'],
    cancelled:['İptal',  'bg-red-50 text-red-700 border-red-200'],
  };
  const [label, cls] = map[status] || [status, 'bg-slate-50 text-slate-600 border-slate-200'];
  return <span className={`inline-block text-[10px] px-1.5 py-0 rounded border font-semibold uppercase ${cls}`}>{label}</span>;
}
